import { isEmpty, merge } from 'lodash-es';
import * as Domains from '../domains';
import { DEV } from '../env/constants';
import { RefreshError } from '../errors';
import * as Location from '../location';
import { ConfigUrl, Type } from '../types';
import * as Url from '../url';
import { defaultedOpt } from '../util';
import { AuthState } from './AuthState';
import BaseAuthService, {
  BaseAuthHooks,
  BaseAuthNames,
  BaseAuthServiceOptions,
  DEFAULT_DELEGATE_USER_PARAM,
} from './BaseAuthService';
import { AuthService, DelegateUserInterceptor, DelegateUserSource } from './types';

interface RefreshResponse {
  expiresAtMs: number;
  refresh: {
    expiresAtMs: number;
    issuedAtMs: number;
  };
}

// these values are defaulted here, but their defaults may be overridden by
// the auth service construction process...to essentially a different default
// to the outside user, especially for common values like customerWebView and
// requireAuthentication
const DEFAULT_OPTIONS: Partial<InternalAuthServiceOptions> = {
  authUrl: {
    path: '/v0/ciam-public/me',
    scheme: 'https',
  },
  loginUrl: {
    path: '/nitro_oauth-common-callback/login',
    scheme: 'https',
  },
  minInitialTtlSeconds: 5 /* minutes */ * 60,
  names: {
    paramDelegateUser: DEFAULT_DELEGATE_USER_PARAM,
    paramLoginReturnUrl: 'goto',
  },
  refreshUrl: {
    path: '/nitro_oauth-common-callback/refresh',
    scheme: 'https',
  },
};

function defaultDelegateUserSource(paramName: string) {
  return (location: Location) => {
    return Location.queryParamValue(location, paramName);
  };
}

// given an environment, returns the options specific to that environment
function defaultOptionsForEnv(
  location: Location,
  env: string,
  baseDomain: string,
): Partial<InternalAuthServiceOptions> {
  const cloudApiDomain = Domains.Internal.cloudApiDomain(env, baseDomain);
  const options: Partial<InternalAuthServiceOptions> = {
    authUrl: {
      host: cloudApiDomain,
    },
    hooks: {
      loginReturnUrlBuilder: () => location.href,
    },
    loginUrl: {
      host: cloudApiDomain,
    },
    logoutUrls: [`https://${cloudApiDomain}/nitro_oauth-common-callback/logout`],
    refreshOnVerify: false,
    refreshUrl: {
      host: cloudApiDomain,
    },
  };

  if (env === DEV) {
    options.echoUrl = { host: cloudApiDomain };
  }

  return options;
}

// Public
// ======

export interface InternalAuthHooks extends BaseAuthHooks {
  // a function that is passed the delegate user id and a function to call to actually
  // delegate the user...if that function isn't called, the user delegation won't happen
  delegateUserInterceptor?: DelegateUserInterceptor;
  delegateUserSource?: DelegateUserSource;
}

export interface InternalAuthNames extends BaseAuthNames {
  // the name of the delegateUser query param
  paramDelegateUser: string;
}

export interface InternalAuthServiceOptions extends BaseAuthServiceOptions {
  // a set of overridable/pluggable function-based behavior, see their type
  // definition for more details
  hooks?: InternalAuthHooks;
  // the configured url for internal login
  loginUrl?: ConfigUrl;
  // urls to load to log the user out
  logoutUrls?: string[];
  // if there are fewer than this many seconds left before the oauth token expires, it will be
  // automatically refreshed during verifyAuthentication
  minInitialTtlSeconds?: number;
  // names for various pieces of functionality (header names, param names), see
  // their type definition for more details
  names?: Partial<InternalAuthNames>;
  // will call refreshToken() as part of verifyAuthentication() if enabled, useful for intentionally
  // long-sessioned applications, disabled by default
  refreshOnVerify?: boolean;
  // the configured url for an endpoint to refresh oauth tokens (ex: common callback's /refresh)
  refreshUrl?: ConfigUrl;
}

function withDefaults(options: InternalAuthServiceOptions): InternalAuthServiceOptions {
  const collapsedOptions = merge(
    {},
    DEFAULT_OPTIONS,
    defaultOptionsForEnv(options.location, options.environment, options.baseDomain),
    options,
  );
  // this hook relies on one of the names so can't be part of the above automatic coalesce
  if (
    collapsedOptions.hooks &&
    collapsedOptions.names?.paramDelegateUser &&
    !collapsedOptions.hooks?.delegateUserSource
  ) {
    collapsedOptions.hooks.delegateUserSource = defaultDelegateUserSource(
      collapsedOptions.names.paramDelegateUser,
    );
  }
  // for whatever reason, in IE11 the location is changed in a way that makes it no longer
  // work by the above merge(), since the location isn't being defaulted, this reassigns
  // it so it will work again
  collapsedOptions.location = options.location;
  return collapsedOptions;
}

/**
 * An auth service that handles internal users (TRP associates). See
 * verifyAuthentication() for a brief description of the internal auth workflow.
 *
 * While this can be used directly, it is likely that the newAuthService()
 * constructor is desirable as it supports common and automatic confiugration.
 */
export default class InternalAuthService extends BaseAuthService implements AuthService {
  private readonly hooks: InternalAuthHooks;
  private readonly loginUrl: ConfigUrl;
  private readonly logoutUrls: string[];
  private readonly minInitialTtlMs: number;
  private readonly names: InternalAuthNames;
  private readonly refreshOnVerify: boolean;
  private readonly refreshUrl: ConfigUrl;

  constructor(options: InternalAuthServiceOptions) {
    options = withDefaults(options);
    super(options);
    this.minInitialTtlMs = defaultedOpt(options.minInitialTtlSeconds) * 1000;
    this.hooks = defaultedOpt(options.hooks);
    this.loginUrl = defaultedOpt(options.loginUrl);
    this.logoutUrls = defaultedOpt(options.logoutUrls);
    this.names = defaultedOpt(options.names) as InternalAuthNames;
    this.refreshOnVerify = defaultedOpt(options.refreshOnVerify);
    this.refreshUrl = defaultedOpt(options.refreshUrl);
  }

  private async fetchRefresh(): Promise<RefreshResponse> {
    const refreshUrl = Location.buildUrl(this.location, this.refreshUrl);
    const response = await this.fetchWithInterceptor(
      refreshUrl,
      this.requestOptions.options().build(),
    );
    if (!response.ok) {
      const responseBody = await response.text();
      throw new RefreshError(responseBody);
    }
    return response.json();
  }

  /**
   * For the internal auth service, the customerWebView is handled by
   * redirecting to login with the addition of a delegateUser query param.
   */
  private async handleDelegateUser() {
    if (!this.customerWebViewEnabled) {
      return undefined;
    }
    const delegateUser = this.hooks?.delegateUserSource?.(this.location);
    if (!delegateUser) {
      return undefined;
    }
    const handleDelegateUser = () => {
      return this.login();
    };
    if (this.hooks?.delegateUserInterceptor) {
      // if there's an interceptor, use that
      return this.hooks.delegateUserInterceptor(delegateUser, handleDelegateUser);
    }
    return handleDelegateUser();
  }

  private async handleRefreshOnVerify(authState: AuthState) {
    if (!this.refreshOnVerify && !this.underMinInitialTtlMs(authState)) {
      return authState;
    }
    try {
      return await this.refreshAuthState(authState);
    } catch (err) {
      console.error('Could not automatically refresh token due to short ttl.', err);
      // this returns the original authState, since even if the refresh failed the user is
      // authenticated for some period of time still
      return authState;
    }
  }

  private loginQueryParams(returnUrl?: ConfigUrl | null) {
    const query: Record<string, string> = {};
    const delegateUser = this.hooks.delegateUserSource?.(this.location);
    if (delegateUser) {
      query[this.names.paramDelegateUser] = delegateUser;
    }
    returnUrl = this.coerceReturnUrl(returnUrl);
    if (returnUrl) {
      // internal login does not support passing query parameters in the return url
      returnUrl = Url.withoutQuery(returnUrl);
      query[this.names.paramLoginReturnUrl] = Location.buildUrl(this.location, returnUrl);
    }
    return query;
  }

  private logoutWithUrl(url: string) {
    return new Promise((resolve) => {
      const iframe = document.createElement('iframe');
      iframe.setAttribute('style', 'display:none;');
      iframe.onload = resolve;
      iframe.setAttribute('sandbox', '');
      iframe.src = url;
      if (document.body) {
        document.body.appendChild(iframe);
      }
      return iframe;
    });
  }

  private underMinInitialTtlMs(authState: AuthState) {
    if (!authState.authenticated) {
      return false;
    }
    if (!authState.expiresAt) {
      // without an expiresAt value, no adjustments can be made
      return false;
    }
    const ttlMs = authState.expiresAt - Date.now();
    // the token will not live long enough in its current state
    return ttlMs <= this.minInitialTtlMs;
  }

  protected async afterAuthenticated(authState: AuthState): Promise<AuthState> {
    authState = await super.afterAuthenticated(authState);
    return this.handleRefreshOnVerify(authState);
  }

  protected async beforeAuthentication(): Promise<void> {
    await this.handleDelegateUser();
    await super.beforeAuthentication();
  }

  /**
   * Handles the low-level operation of refreshing the auth state's token. This encompasses making
   * the HTTP hit, but doesn't do any state management or event firing.
   *
   * @param authState
   */
  private async refreshAuthState(authState?: AuthState) {
    if (!authState) {
      // because refreshing does not update any details about the token, its previous values (user
      // id, entitlements, etc) are used, with its expiration updated...if those details are not
      // available for some reason, then the refresh would be operating on an invalid auth state
      throw new Error('Cannot refresh, missing auth state. Use verifyAuthentication().');
    }
    if (!authState.authenticated) {
      // no reason to expect refresh to work if the user isn't currently authenticated
      throw new Error('Cannot refresh unauthenticated token. Use login().');
    }
    const response = await this.fetchRefresh();
    if (!response.expiresAtMs) {
      throw new Error('Refresh token response is missing expiration.');
    }
    return {
      ...authState,
      expiresAt: response.expiresAtMs,
      refresh: {
        ...authState.refresh,
        expiresAt: response.refresh?.expiresAtMs,
      },
    };
  }

  // AuthService
  // ===========

  buildLoginUrl(returnUrl?: string | null) {
    const loginUrl = Url.withQueryParams(this.loginUrl, this.loginQueryParams(returnUrl));
    return Location.buildUrl(this.location, loginUrl);
  }

  async logout(gotoUrl?: string | null): Promise<void> {
    if (isEmpty(this.logoutUrls)) {
      console.warn('No logoutUrls configured.');
      return;
    }
    await Promise.all(this.logoutUrls.map((url) => this.logoutWithUrl(url)));
    if (gotoUrl) {
      window.location.assign(gotoUrl);
    }
  }

  /**
   * Refreshes the oauth token for the current auth service. This token is unavailable to the
   * browser. It is refreshed via a server hit that is passing the refresh token via a cookie. To
   * use this endpoint, verify a refresh is possible (the session is internal, authenticated, etc.)
   * but assume the refresh could still fail.
   *
   * Returns the updated authState (with an updated expiresAt value) if the refresh succeeded. If
   * the refresh doesn't succeed, an error is thrown.
   */
  async refreshToken() {
    const refreshedAuthState = await this.refreshAuthState(this.lastAuthState);
    return this.authStateChanged(refreshedAuthState);
  }

  type(): Type {
    return 'internal';
  }
}
